export const CONFIGURACION_INICIAL_RUTA = "/App/ConfiguracionInicial";

export const ESTADOS_PROCESO = Object.freeze({
  PENDIENTE: "PENDIENTE",
  EN_PROGRESO: "EN_PROGRESO",
  COMPLETADA: "COMPLETADA",
  OMITIDA: "OMITIDA",
});

export const ESTADOS_RESPUESTA = Object.freeze({
  SIN_RESPONDER: "SIN_RESPONDER",
  RESPONDIDA: "RESPONDIDA",
  PARA_DESPUES: "PARA_DESPUES",
  OMITIDA: "OMITIDA",
});

export const ESTADOS_PLAN = Object.freeze({
  GENERADO: "GENERADO",
  FINAL: "FINAL",
});

export const CODIGO_OCURRENCIA_UNICA = "UNICA";
export const CODIGO_OCURRENCIA_OMISION_PREGUNTA = "OMISION_PREGUNTA";

export const esEstadoProcesoDisponible = (estado) =>
  estado === ESTADOS_PROCESO.PENDIENTE ||
  estado === ESTADOS_PROCESO.EN_PROGRESO;

export const esRespuestaResuelta = (estado) =>
  estado === ESTADOS_RESPUESTA.RESPONDIDA ||
  estado === ESTADOS_RESPUESTA.OMITIDA;

export const crearClaveRespuesta = (codigoElemento, codigoOcurrencia) =>
  `${codigoElemento || ""}::${codigoOcurrencia || CODIGO_OCURRENCIA_UNICA}`;

export const obtenerPrimero = (objeto, nombres, valorDefecto = undefined) => {
  if (!objeto) return valorDefecto;
  for (const nombre of nombres) {
    if (Object.prototype.hasOwnProperty.call(objeto, nombre)) {
      return objeto[nombre];
    }
  }
  return valorDefecto;
};

export const esPlanVigente = (plan, proceso) => {
  const versionOrigen = obtenerPrimero(plan, ["VersionProcesoOrigen", "versionProcesoOrigen"], null);
  const versionActual = obtenerPrimero(
    proceso,
    ["VersionRegistroProceso", "versionRegistroProceso", "VersionRegistro", "versionRegistro"],
    null
  );
  if (versionOrigen === null || versionOrigen === undefined ||
    versionActual === null || versionActual === undefined) return false;
  return String(versionOrigen) === String(versionActual);
};

export const parsearJsonSeguro = (valor, valorDefecto = null) => {
  if (valor === null || valor === undefined || valor === "") return valorDefecto;
  if (typeof valor !== "string") return valor;
  try {
    return JSON.parse(valor);
  } catch (_error) {
    return valor;
  }
};

export const serializarRespuesta = (valor) => {
  if (valor === undefined) return null;
  return JSON.stringify(valor);
};

const tienePropiedad = (objeto, nombres) => Boolean(objeto) && nombres.some((nombre) =>
  Object.prototype.hasOwnProperty.call(objeto, nombre)
);

const separarClaveRelacionada = (clave) => {
  const texto = String(clave || "");
  const separador = texto.indexOf("::");
  if (separador < 0) return [texto, CODIGO_OCURRENCIA_UNICA];
  return [texto.slice(0, separador), texto.slice(separador + 2) || CODIGO_OCURRENCIA_UNICA];
};

const normalizarRelacionado = (entrada, codigoElementoDefecto, codigoOcurrenciaDefecto, indice) => {
  const esObjeto = entrada !== null && typeof entrada === "object" && !Array.isArray(entrada);
  const item = esObjeto ? entrada : { Valor: entrada };
  const propiedadesContenido = [
    "ContenidoRespuestaJson", "contenidoRespuestaJson", "ValorRespuestaJson", "valorRespuestaJson",
    "Contenido", "contenido", "Valor", "valor", "Sugerencia", "sugerencia",
  ];
  const contenidoCrudo = tienePropiedad(item, propiedadesContenido)
    ? obtenerPrimero(item, propiedadesContenido, null)
    : item;
  const codigoElemento = obtenerPrimero(
    item,
    ["CodigoElemento", "codigoElemento", "Codigo", "codigo", "ElementId", "elementId"],
    codigoElementoDefecto || `ELEMENTO_${indice + 1}`
  );
  const codigoOcurrencia = obtenerPrimero(
    item,
    ["CodigoOcurrencia", "codigoOcurrencia", "OccurrenceCode", "occurrenceCode"],
    codigoOcurrenciaDefecto || CODIGO_OCURRENCIA_UNICA
  ) || CODIGO_OCURRENCIA_UNICA;
  return {
    ...item,
    CodigoElemento: codigoElemento,
    CodigoOcurrencia: codigoOcurrencia,
    ContenidoRespuesta: parsearJsonSeguro(contenidoCrudo, contenidoCrudo),
    Fuente: obtenerPrimero(item, ["Fuente", "fuente", "Source", "source"], "Citrus ERP"),
    Estado: obtenerPrimero(
      item,
      ["Estado", "estado", "EstadoLectura", "estadoLectura", "Status", "status"],
      null
    ),
    Mensaje: obtenerPrimero(item, ["Mensaje", "mensaje", "Descripcion", "descripcion", "Message", "message"], ""),
  };
};

/**
 * Admite el contrato nuevo como arreglo y, para una transición segura, también
 * diccionarios indexados por CodigoElemento o CodigoElemento::Ocurrencia.
 */
export const normalizarRelacionados = (origen) => {
  if (origen === null || origen === undefined) return [];
  const contenido = obtenerPrimero(
    origen,
    ["Elementos", "elementos", "Items", "items", "Valores", "valores"],
    origen
  );
  if (Array.isArray(contenido)) {
    return contenido.map((item, indice) => normalizarRelacionado(item, null, null, indice));
  }
  if (typeof contenido !== "object") return [];
  return Object.entries(contenido).flatMap(([clave, valor], indice) => {
    const [codigoElemento, codigoOcurrencia] = separarClaveRelacionada(clave);
    const entradas = Array.isArray(valor) ? valor : [valor];
    return entradas.map((item, subindice) => normalizarRelacionado(
      item,
      codigoElemento,
      entradas.length > 1 && codigoOcurrencia === CODIGO_OCURRENCIA_UNICA
        ? `CITRUS-${subindice + 1}`
        : codigoOcurrencia,
      indice + subindice
    ));
  });
};

export const crearIndiceRelacionados = (relacionados) => {
  const indice = new Map();
  (relacionados || []).forEach((item) => {
    indice.set(crearClaveRespuesta(item.CodigoElemento, item.CodigoOcurrencia), item);
  });
  return indice;
};

export const contenidoRelacionadoDe = (relacionado) => obtenerPrimero(
  relacionado,
  ["ContenidoRespuesta", "contenidoRespuesta"],
  parsearJsonSeguro(obtenerPrimero(
    relacionado,
    ["ContenidoRespuestaJson", "contenidoRespuestaJson", "ValorRespuestaJson", "valorRespuestaJson"],
    null
  ), null)
);

const normalizarValorPermitido = (opcion, indice) => ({
  ...opcion,
  Valor: obtenerPrimero(opcion, ["Valor", "valor", "Value", "value"], `VALOR_${indice + 1}`),
  Etiqueta: obtenerPrimero(opcion, ["Etiqueta", "etiqueta", "Label", "label", "Nombre", "nombre"], "Opción"),
});

const normalizarOpcion = (opcion, indice) => {
  const valoresPermitidos = obtenerPrimero(opcion, ["ValoresPermitidos", "valoresPermitidos", "Choices", "choices"], []);
  return {
    ...opcion,
    Codigo: obtenerPrimero(opcion, ["Codigo", "codigo", "Id", "id", "Valor", "valor"], `OPCION_${indice + 1}`),
    Valor: obtenerPrimero(opcion, ["Valor", "valor", "Value", "value", "Codigo", "codigo", "Id", "id"], null),
    Etiqueta: obtenerPrimero(opcion, ["Etiqueta", "etiqueta", "Texto", "texto", "Nombre", "nombre", "Label", "label"], "Opción"),
    Descripcion: obtenerPrimero(opcion, ["Descripcion", "descripcion", "Ayuda", "ayuda"], ""),
    TipoEntrada: String(obtenerPrimero(opcion, ["TipoEntrada", "tipoEntrada", "InputKind", "inputKind"], "OPTION")).toUpperCase(),
    ControlEntrada: String(obtenerPrimero(opcion, ["ControlEntrada", "controlEntrada", "ControlKind", "controlKind"], "TEXT")).toUpperCase(),
    ValoresPermitidos: Array.isArray(valoresPermitidos) ? valoresPermitidos.map(normalizarValorPermitido) : [],
  };
};

const normalizarElemento = (elemento, indice) => {
  const tipo = String(obtenerPrimero(elemento, ["Tipo", "tipo", "TipoElemento", "tipoElemento", "Kind", "kind"], "QUESTION")).toUpperCase();
  const opciones = obtenerPrimero(elemento, ["Opciones", "opciones", "Options", "options", "RespuestasPermitidas", "respuestasPermitidas"], []);
  const campos = obtenerPrimero(elemento, ["Campos", "campos", "Fields", "fields", "Preguntas", "preguntas"], []);
  return {
    ...elemento,
    Codigo: obtenerPrimero(elemento, ["Codigo", "codigo", "CodigoElemento", "codigoElemento", "Id", "id"], `ELEMENTO_${indice + 1}`),
    Tipo: tipo,
    Orden: Number(obtenerPrimero(elemento, ["Orden", "orden"], indice + 1)),
    Titulo: obtenerPrimero(elemento, ["Titulo", "titulo", "Title", "title", "Pregunta", "pregunta", "Texto", "texto", "PreguntaORegla"], "Pregunta"),
    Ayuda: obtenerPrimero(elemento, ["Ayuda", "ayuda", "Help", "help", "AyudaBreve", "ayudaBreve", "Descripcion", "descripcion"], ""),
    TipoRespuesta: String(obtenerPrimero(elemento, ["TipoRespuesta", "tipoRespuesta", "ResponseType", "responseType", "Control", "control"], "FORM")).toUpperCase(),
    Aplicable: obtenerPrimero(elemento, ["Aplicable", "aplicable", "EsAplicable", "esAplicable"], true) !== false,
    RequeridoParaCompletar: obtenerPrimero(elemento, ["RequeridoParaCompletar", "requeridoParaCompletar", "RequiredToComplete", "requiredToComplete"], false) === true,
    PuedeOmitirse: obtenerPrimero(elemento, ["PuedeOmitirse", "puedeOmitirse", "CanOmit", "canOmit"], true) !== false,
    EsRepetible: obtenerPrimero(elemento, ["EsRepetible", "esRepetible"], false) === true ||
      obtenerPrimero(elemento, ["Repetible", "repetible"], false) === true ||
      obtenerPrimero(obtenerPrimero(elemento, ["Repetible", "repetible", "Repeatable", "repeatable"], {}), ["Habilitado", "habilitado", "Enabled", "enabled"], false) === true ||
      String(obtenerPrimero(elemento, ["TipoRespuesta", "tipoRespuesta", "ResponseType", "responseType"], "")).toUpperCase() === "REPEATABLE_FORM",
    Repetible: obtenerPrimero(elemento, ["Repetible", "repetible", "Repeatable", "repeatable"], null),
      Opciones: Array.isArray(opciones) ? opciones.map(normalizarOpcion) : [],
      CamposDetalle: (elemento.detailFields || elemento.DetailFields || []).map(normalizarOpcion),
    Campos: Array.isArray(campos) ? campos.map(normalizarElemento) : [],
  };
};

const esReglaInterna = (elemento) => {
  const tipo = String(elemento.Tipo || "").replace(/\s+/g, "_").toUpperCase();
  return tipo === "REGLA" || tipo === "REGLA_INTERNA" || tipo === "INTERNAL_RULE" || tipo === "SISTEMA";
};

const normalizarSeccion = (seccion, indice) => {
  const elementos = obtenerPrimero(seccion, ["Elementos", "elementos", "Preguntas", "preguntas"], []);
  return {
    ...seccion,
    Codigo: obtenerPrimero(seccion, ["Codigo", "codigo", "CodigoSeccion", "codigoSeccion", "Id", "id"], `SECCION_${indice + 1}`),
    Orden: Number(obtenerPrimero(seccion, ["Orden", "orden"], indice + 1)),
    Titulo: obtenerPrimero(seccion, ["Titulo", "titulo", "Title", "title", "Nombre", "nombre"], `Sección ${indice + 1}`),
    Descripcion: obtenerPrimero(seccion, ["Descripcion", "descripcion", "Ayuda", "ayuda"], ""),
    Aplicable: obtenerPrimero(seccion, ["Aplicable", "aplicable", "EsAplicable", "esAplicable"], true) !== false,
    Elementos: (Array.isArray(elementos) ? elementos : [])
      .map(normalizarElemento)
      .filter((elemento) => elemento.Aplicable && !esReglaInterna(elemento))
      .sort((a, b) => a.Orden - b.Orden),
  };
};

const agruparElementosPlanos = (elementos) => {
  const bloques = new Map();
  elementos.forEach((elemento, indice) => {
    const codigoBloque = obtenerPrimero(elemento, ["CodigoBloque", "codigoBloque", "Etapa", "etapa"], "GENERAL");
    const tituloBloque = obtenerPrimero(elemento, ["TituloBloque", "tituloBloque", "Etapa", "etapa"], "Configuración inicial");
    const codigoSeccion = obtenerPrimero(elemento, ["CodigoSeccion", "codigoSeccion", "Subseccion", "Subsección", "seccion"], "GENERAL");
    const tituloSeccion = obtenerPrimero(elemento, ["TituloSeccion", "tituloSeccion", "Subseccion", "Subsección", "seccion"], "General");
    if (!bloques.has(codigoBloque)) {
      bloques.set(codigoBloque, {
        Codigo: codigoBloque,
        Titulo: tituloBloque,
        Orden: bloques.size + 1,
        Secciones: new Map(),
      });
    }
    const bloque = bloques.get(codigoBloque);
    if (!bloque.Secciones.has(codigoSeccion)) {
      bloque.Secciones.set(codigoSeccion, {
        Codigo: codigoSeccion,
        Titulo: tituloSeccion,
        Orden: bloque.Secciones.size + 1,
        Elementos: [],
      });
    }
    bloque.Secciones.get(codigoSeccion).Elementos.push({ ...elemento, Orden: obtenerPrimero(elemento, ["Orden", "orden"], indice + 1) });
  });
  return Array.from(bloques.values()).map((bloque) => ({
    ...bloque,
    Secciones: Array.from(bloque.Secciones.values()),
  }));
};

export const normalizarDefinicion = (definicion) => {
  if (!definicion) return { Version: null, Bloques: [] };
  const elementosPlanos = obtenerPrimero(definicion, ["Elementos", "elementos", "Elements", "elements"], []);
  const seccionesPlanas = obtenerPrimero(definicion, ["Secciones", "secciones", "Sections", "sections"], []);
  const bloquesOriginales = obtenerPrimero(definicion, ["Bloques", "bloques", "Blocks", "blocks", "Etapas", "etapas"], []);
  const bloques = Array.isArray(bloquesOriginales) && bloquesOriginales.length
    ? bloquesOriginales
    : agruparElementosPlanos(Array.isArray(elementosPlanos) ? elementosPlanos : []);

  return {
    ...definicion,
    Version: obtenerPrimero(definicion, ["Version", "version", "VersionDefinicion", "versionDefinicion"], null),
    Bloques: bloques.map((bloque, indice) => {
      const idsSeccion = obtenerPrimero(bloque, ["IdsSeccion", "idsSeccion", "SectionIds", "sectionIds"], []);
      const codigoBloque = obtenerPrimero(bloque, ["Codigo", "codigo", "CodigoBloque", "codigoBloque", "Id", "id"], `BLOQUE_${indice + 1}`);
      let secciones = obtenerPrimero(bloque, ["Secciones", "secciones"], []);
      if ((!Array.isArray(secciones) || secciones.length === 0) && Array.isArray(seccionesPlanas)) {
        secciones = seccionesPlanas.filter((seccion) => {
          const codigoSeccion = obtenerPrimero(seccion, ["Codigo", "codigo", "CodigoSeccion", "codigoSeccion", "Id", "id"]);
          const seccionBloque = obtenerPrimero(seccion, ["CodigoBloque", "codigoBloque", "BlockId", "blockId"]);
          return (Array.isArray(idsSeccion) && idsSeccion.includes(codigoSeccion)) || seccionBloque === codigoBloque;
        }).map((seccion) => {
          const idsElemento = obtenerPrimero(seccion, ["IdsElemento", "idsElemento", "ElementIds", "elementIds"], []);
          const codigoSeccion = obtenerPrimero(seccion, ["Codigo", "codigo", "CodigoSeccion", "codigoSeccion", "Id", "id"]);
          const elementos = (Array.isArray(elementosPlanos) ? elementosPlanos : []).filter((elemento) => {
            const codigoElemento = obtenerPrimero(elemento, ["Codigo", "codigo", "CodigoElemento", "codigoElemento", "Id", "id"]);
            const elementoSeccion = obtenerPrimero(elemento, ["CodigoSeccion", "codigoSeccion", "SectionId", "sectionId"]);
            return (Array.isArray(idsElemento) && idsElemento.includes(codigoElemento)) || elementoSeccion === codigoSeccion;
          });
          return { ...seccion, Elementos: elementos };
        });
      }
      return {
        ...bloque,
        Codigo: codigoBloque,
        Orden: Number(obtenerPrimero(bloque, ["Orden", "orden"], indice + 1)),
        Titulo: obtenerPrimero(bloque, ["Titulo", "titulo", "Title", "title", "Nombre", "nombre"], `Bloque ${indice + 1}`),
        Descripcion: obtenerPrimero(bloque, ["Descripcion", "descripcion", "Ayuda", "ayuda"], ""),
        Aplicable: obtenerPrimero(bloque, ["Aplicable", "aplicable", "EsAplicable", "esAplicable"], true) !== false,
        Secciones: (Array.isArray(secciones) ? secciones : [])
          .map(normalizarSeccion)
          .filter((seccion) => seccion.Aplicable && seccion.Elementos.length > 0)
          .sort((a, b) => a.Orden - b.Orden),
      };
    }).filter((bloque) => bloque.Aplicable && bloque.Secciones.length > 0).sort((a, b) => a.Orden - b.Orden),
  };
};

export const normalizarCarga = (respuesta) => {
  const raiz = obtenerPrimero(respuesta, ["ConfiguracionInicial", "configuracionInicial", "Resultado", "resultado"], respuesta || {});
  const proceso = obtenerPrimero(raiz, ["Proceso", "proceso", "ConfiguracionInicialProceso"], null);
  const definicion = obtenerPrimero(raiz, ["Definicion", "definicion", "Manifiesto", "manifiesto"], null);
  const respuestas = obtenerPrimero(raiz, ["Respuestas", "respuestas", "ConfiguracionInicialRespuestas"], []);
  const plan = obtenerPrimero(raiz, ["Plan", "plan", "ConfiguracionInicialPlan"], null);
  const aplicabilidadElementos = obtenerPrimero(raiz, ["AplicabilidadElementos", "aplicabilidadElementos"], {}) || {};
  const aplicabilidadSecciones = obtenerPrimero(raiz, ["AplicabilidadSecciones", "aplicabilidadSecciones"], {}) || {};
  const nombresValoresActuales = ["ValoresActuales", "valoresActuales", "CurrentValues", "currentValues"];
  const nombresSugerencias = ["Sugerencias", "sugerencias", "Suggestions", "suggestions"];
  const nombresDiferencias = ["Diferencias", "diferencias", "Differences", "differences"];
  const contextoIntegracionDisponible = tienePropiedad(raiz, [
    ...nombresValoresActuales,
    ...nombresSugerencias,
    ...nombresDiferencias,
  ]);
  const habilitacionExplicita = obtenerPrimero(raiz, [
    "PuedeAplicarConfiguracion", "puedeAplicarConfiguracion", "PuedeAplicar", "puedeAplicar",
    "AplicacionHabilitada", "aplicacionHabilitada",
  ], undefined);
  const modoOperativoExplicito = obtenerPrimero(raiz, [
    "AplicacionOperativaHabilitada", "aplicacionOperativaHabilitada",
    "ModoAplicacionOperativa", "modoAplicacionOperativa",
  ], undefined);
  const definicionNormalizada = normalizarDefinicion(definicion);
  definicionNormalizada.Bloques = definicionNormalizada.Bloques.map((bloque) => ({
    ...bloque,
    Secciones: bloque.Secciones.map((seccion) => ({
      ...seccion,
      Aplicable: aplicabilidadSecciones[seccion.Codigo] !== false,
      Elementos: seccion.Elementos.filter((elemento) => aplicabilidadElementos[elemento.Codigo] !== false),
    })).filter((seccion) => seccion.Aplicable && seccion.Elementos.length > 0),
  })).filter((bloque) => bloque.Secciones.length > 0);
  return {
    raiz,
    proceso,
    definicion: definicionNormalizada,
    respuestas: Array.isArray(respuestas) ? respuestas : [],
    plan,
    progresoServidor: obtenerPrimero(raiz, ["Progreso", "progreso"], obtenerPrimero(proceso, ["Progreso", "progreso"], null)),
    aplicabilidadElementos,
    aplicabilidadSecciones,
    seccionesOmitidas: obtenerPrimero(raiz, ["SeccionesOmitidas", "seccionesOmitidas"], []) || [],
    valoresActuales: normalizarRelacionados(obtenerPrimero(raiz, nombresValoresActuales, [])),
    sugerencias: normalizarRelacionados(obtenerPrimero(raiz, nombresSugerencias, [])),
    diferencias: normalizarRelacionados(obtenerPrimero(raiz, nombresDiferencias, [])),
    contextoIntegracionDisponible,
    aplicacionOperativaHabilitada: modoOperativoExplicito === undefined
      ? habilitacionExplicita === true
      : modoOperativoExplicito === true,
    puedeAplicarConfiguracion: habilitacionExplicita === undefined
      ? contextoIntegracionDisponible
      : habilitacionExplicita === true,
  };
};

export const calcularProgreso = (definicion, respuestas) => {
  const indiceRespuestas = new Map();
  (respuestas || []).forEach((respuesta) => {
    const codigo = obtenerPrimero(respuesta, ["CodigoElemento", "codigoElemento", "Codigo", "codigo"]);
    const ocurrencia = obtenerPrimero(respuesta, ["CodigoOcurrencia", "codigoOcurrencia"], CODIGO_OCURRENCIA_UNICA);
    indiceRespuestas.set(crearClaveRespuesta(codigo, ocurrencia), respuesta);
  });
  const preguntas = (definicion?.Bloques || []).flatMap((bloque) =>
    bloque.Secciones.flatMap((seccion) => seccion.Elementos.filter((elemento) => elemento.Aplicable))
  );
  let resueltas = 0;
  let paraDespues = 0;
  let omitidas = 0;
  preguntas.forEach((pregunta) => {
    if (pregunta.EsRepetible || pregunta.TipoRespuesta === "REPEATABLE_FORM") {
      const asociadas = (respuestas || []).filter((respuesta) =>
        obtenerPrimero(respuesta, ["CodigoElemento", "codigoElemento", "Codigo", "codigo"]) === pregunta.Codigo
      );
      const marcador = asociadas.find((respuesta) =>
        obtenerPrimero(respuesta, ["CodigoOcurrencia", "codigoOcurrencia"], CODIGO_OCURRENCIA_UNICA) === CODIGO_OCURRENCIA_OMISION_PREGUNTA
      );
      const operativas = asociadas.filter((respuesta) =>
        obtenerPrimero(respuesta, ["CodigoOcurrencia", "codigoOcurrencia"], CODIGO_OCURRENCIA_UNICA) !== CODIGO_OCURRENCIA_OMISION_PREGUNTA
      );
      if (obtenerPrimero(marcador, ["EstadoRespuesta", "estadoRespuesta", "Estado", "estado"]) === ESTADOS_RESPUESTA.OMITIDA) {
        resueltas += 1;
        omitidas += 1;
      } else if (operativas.length > 0 && operativas.every((respuesta) => esRespuestaResuelta(
        obtenerPrimero(respuesta, ["EstadoRespuesta", "estadoRespuesta", "Estado", "estado"])
      ))) {
        resueltas += 1;
      } else if (operativas.some((respuesta) =>
        obtenerPrimero(respuesta, ["EstadoRespuesta", "estadoRespuesta", "Estado", "estado"]) === ESTADOS_RESPUESTA.PARA_DESPUES
      )) {
        paraDespues += 1;
      }
      return;
    }
    const respuesta = indiceRespuestas.get(crearClaveRespuesta(pregunta.Codigo, CODIGO_OCURRENCIA_UNICA));
    const estado = obtenerPrimero(respuesta, ["EstadoRespuesta", "estadoRespuesta", "Estado", "estado"]);
    if (esRespuestaResuelta(estado)) resueltas += 1;
    if (estado === ESTADOS_RESPUESTA.PARA_DESPUES) paraDespues += 1;
    if (estado === ESTADOS_RESPUESTA.OMITIDA) omitidas += 1;
  });
  const total = preguntas.length;
  return {
    Total: total,
    Resueltas: resueltas,
    Pendientes: Math.max(0, total - resueltas),
    ParaDespues: paraDespues,
    Omitidas: omitidas,
    Porcentaje: total === 0 ? 0 : Math.round((resueltas / total) * 100),
  };
};
